'use client'

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'

export interface UserProfile {
  id?: string
  username?: string
  email?: string
  phone?: string // Backend returns 'phone'
  phone_code?: string // Backend returns 'phone_code'
  phoneNumber?: string // For compatibility
  kyc_status?: string // Backend returns 'kyc_status'
  kycStatus?: string // For compatibility
  uuid?: string
  photo?: string
  full_name?: string // Backend returns 'full_name'
  country?: string
  vipLevel?: number
  createdAt?: string
  updatedAt?: string
}

interface ProfileContextType {
  profile: UserProfile | null
  isLoading: boolean
  error: string | null
  fetchProfile: () => Promise<void>
  updateProfile: (updates: Partial<UserProfile>) => void
  clearProfile: () => void
  refreshProfile: () => Promise<void>
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined)

export function ProfileProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchProfile = useCallback(async () => {
    const isDev = process.env.NODE_ENV === 'development'
    
    try {
      setIsLoading(true)
      setError(null)

      // Get Firebase auth from the imported auth object
      const { auth } = await import('@/app/lib/firebase')
      
      // Wait for auth state to be ready
      if (!auth.currentUser) {
        if (isDev) console.log('[Profile] Waiting for auth state...')
        
        // Wait up to 3 seconds for auth to be ready
        const waitForAuth = new Promise<void>((resolve) => {
          const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
              unsubscribe()
              resolve()
            }
          })
          
          // Timeout after 3 seconds
          setTimeout(() => {
            unsubscribe()
            resolve()
          }, 3000)
        })
        
        await waitForAuth
      }
      
      const currentUser = auth.currentUser
      
      if (!currentUser) {
        if (isDev) console.log('[Profile] No authenticated user, skipping fetch')
        setIsLoading(false)
        return
      }

      const idToken = await currentUser.getIdToken()

      const response = await fetch('/api/profile', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.status}`)
      }

      const data = await response.json()

      if (data.status && data.data) {
        setProfile(data.data)
        if (isDev) console.log('[Profile] Loaded successfully')
      } else {
        throw new Error(data.message || 'Failed to load profile')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load profile'
      setError(errorMessage)
      console.error('[Profile] Error:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    await fetchProfile()
  }, [fetchProfile])

  const updateProfile = useCallback((updates: Partial<UserProfile>) => {
    setProfile(prev => prev ? { ...prev, ...updates } : null)
  }, [])

  const clearProfile = useCallback(() => {
    setProfile(null)
    setError(null)
  }, [])

  const value: ProfileContextType = {
    profile,
    isLoading,
    error,
    fetchProfile,
    updateProfile,
    clearProfile,
    refreshProfile,
  }

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  )
}

export function useProfile() {
  const context = useContext(ProfileContext)
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider')
  }
  return context
}
